import { modifier, tablet } from '@styles/mixins'
import { colors } from '@styles/vars'
import React from 'react'
import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { Link as SmoothLink } from 'react-scroll'

/* MAIN */
interface ButtonProps {
    element?: "link" | "a" | "submit" | "smooth",
    children?: React.ReactNode,
    icon?: React.ReactNode,
    to?: string,
    width?: "narrow" | "fixed" | "auto",
    type?: "primary" | "secondary" | "tertiary" | "google" | "facebook",
    scheme?: "reversed" | "accent" | "outline" | "reversed-outline" | "transparent",
    disabled?: boolean,
    isActive?: boolean
    [key: string]: any
}

const Button = ({ icon, type, width, scheme, isActive, children, ...props }: ButtonProps) => {
    const modifiers = { icon, type, width, scheme, isActive, childrenMissing: !children }
    return (
        <Wrapper {...modifiers}>
            <Element {...props}>
                {icon}{children && <span>{children}</span>}
            </Element>
        </Wrapper>
    )
}

export default React.memo(Button)

/* COMPONENTS */
type ElementProps = Omit<ButtonProps, "icon" | "type" | "width">

const Element = ({ element, to, children, ...props }: ElementProps) => {
    switch (element) {
        case 'a': return <a href={to} {...props}>{children}</a>
        case 'link': return <Link to={to} {...props}>{children}</Link>
        case 'submit': return <button {...props}>{children}</button>
        case 'smooth': return <SmoothLink to={to} smooth={true} {...props}>{children}</SmoothLink>
        default: 
            if (to) return <Link to={to} {...props}>{children}</Link>
            else return <button type="button" {...props}>{children}</button>
    }
}

/* STYLES */
type ButtonModifiers = Pick<ButtonProps, "width" | "type" | "icon" | "scheme" | "isActive" | "childrenMissing">

const Wrapper = styled.div<ButtonModifiers>`
    height: 54px;
    width: 100%;

    a {
        text-decoration: none;
    }

    & > a, & > button {
        z-index: 1;
        position: relative;
        height: 100%;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        font-weight: bold;
        border: 0;
        margin: 0;
        border-radius: 27px;
        padding: 0 30px;
        transition: .2s ease-out;
        overflow: hidden;
        text-decoration: none;
        text-align: center;

        svg {
            fill: ${colors.white};
            transition: .2s ease-out;
        }

        div:not(:last-child), svg:not(:last-child) {
            margin-right: 10px;
        }

        &:focus {
            outline: none;
        }

        &:hover {
            cursor: pointer;
        }

        background: ${colors.black};
        color: ${colors.white};

        &:disabled {
            background: ${colors.greyBackground};
            color: ${colors.darkGrey};
            cursor: not-allowed;
        }

        ${modifier("scheme:reversed", css`
            color: ${colors.black};
            background: ${colors.white};

            &:hover {
                color: ${colors.black};
                background: ${colors.white};
            }
        `)}

        ${modifier("scheme:reversed-outline", css`
            color: ${colors.white};
            background: transparent;
            border: 2px solid ${colors.white};

            &:hover {
                background: transparent;
                border: 2px solid ${colors.white};
            }
        `)}

        ${modifier("icon", css`
            padding-right: 40px;
        `)}

        ${modifier("type:secondary", css`
            background: transparent;
            color: ${colors.black};
            border: 2px solid ${colors.black};

            &:hover {
                background: transparent;
                color: ${colors.black};
                border: 2px solid ${colors.black};
            }

            svg {
                fill: ${colors.black};
            }
        `)}

        ${modifier("type:tertiary", css`
            background: ${colors.white};
            border: 1px solid ${colors.border};
            color: ${colors.black};
            padding: 7px 12px;
            width: max-content;

            &:hover {
                background: ${colors.greyBackground};
            }

            &:active {
                background: ${colors.greyBackgroundFocused};
            }

            svg {
                fill: ${colors.black};
                flex-shrink: 0;
            }

            ${modifier("icon", css`
                padding-right: 15px;

                ${modifier("childrenMissing", css`
                    padding: 7px;
                `)}
            `)}

            ${modifier("scheme:accent", css`
                background: ${colors.accentBackground};

                &:hover {
                    background: ${colors.elementBackground};
                }

                &:active {
                    background: ${colors.elementBackgroundFocused};
                }
            `)}

            ${modifier("scheme:transparent", css`
                background: transparent;
                border: 1px solid transparent;
                transition: 0;

                &:hover {
                    background: transparent;
                    border: 1px solid ${colors.border};
                }

                &:active {
                    background: transparent;
                    border: 1px solid ${colors.darkGrey};
                }

                ${modifier("isActive", css`
                    &, &:hover {
                        border: 1px solid ${colors.darkGrey};
                    }
                `)}
            `)}

            ${modifier("scheme:outline", css`
                background: transparent;
                border: 2px solid ${colors.black};
                color: ${colors.black};

                &:hover {
                    background: transparent;
                    border: 2px solid ${colors.black};
                    color: ${colors.black};
                }

                &:active {
                    background: transparent;
                }
            `)}

            ${modifier("scheme:reversed", css`
                color: ${colors.white};
                background: ${colors.darkGrey};
                border: 0;

                &:hover {
                    color: ${colors.white};
                    background: ${colors.darkGrey};
                    cursor: pointer;
                }

                svg {
                    fill: ${colors.white};
                }
            `)}
        `)}

        ${modifier("type:google", css`
            background: ${colors.google};
        `)}

        ${modifier("type:facebook", css`
            background: ${colors.facebook};
        `)}
    }

    ${modifier("type:tertiary", css`
        width: auto;
        height: auto;
    `)}

    ${modifier("width:fixed", css`
        ${tablet(css`width: 250px;`)}
    `)}

    ${modifier("width:auto", css`
        width: auto;
        flex-shrink: 0;
    `)}

    ${modifier("width:narrow", css`
        width: auto;
        max-width: 150px;
        button {
            padding: 0 15px;

            ${modifier("icon", css`
                padding-right: 20px;
            `)}
        }
    `)}
`